import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown, Search } from "lucide-react";

export default function PrincipalSelector({
  scope = "user",
  users = [],
  groups = [],
  selectedId = null,
  onSelect,
  limit = 10,
  disabled = false,
  disabledReason = "",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  const options = scope === "user" ? users : groups;
  const selected = options.find((o) => o.id === selectedId);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options.slice(0, limit);
    return options
      .filter((o) => {
        const nameHit = String(o.name || "").toLowerCase().includes(q);
        const emailHit = String(o.email || "").toLowerCase().includes(q);
        return nameHit || emailHit;
      })
      .slice(0, limit);
  }, [options, search, limit]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const placeholder = scope === "user" ? "Select user..." : "Select group...";

  return (
    <div className="relative" ref={ref} title={disabled ? disabledReason : ""}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((p) => !p);
        }}
        className={`w-full h-10 px-3 flex items-center justify-between rounded-md border text-sm ${
          disabled
            ? "border-border/70 bg-muted/40 text-muted-foreground cursor-not-allowed"
            : "border-border bg-background"
        }`}
      >
        <span className="truncate text-left">
          {selected ? `${selected.name}${selected.email ? ` (${selected.email})` : ""}` : placeholder}
        </span>
        <ChevronsUpDown size={14} />
      </button>

      {open && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50">
          <div className="p-2 relative">
            <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${scope}s...`}
              className="w-full h-8 pl-8 pr-2 rounded-md border border-border bg-background text-xs"
            />
          </div>

          <div className="max-h-56 overflow-y-auto px-1 pb-1">
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-center text-muted-foreground">No results</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    onSelect?.(opt.id);
                    setOpen(false);
                    setSearch("");
                  }}
                  className="w-full text-left px-3 py-2 rounded hover:bg-muted text-xs"
                >
                  <div className="font-medium">{opt.name}</div>
                  {opt.email && <div className="text-[10px] text-muted-foreground">{opt.email}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
